import * as React from 'react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'

export interface DropdownOption {
  value: string
  count?: number
}

interface MultiSelectDropdownProps {
  label: string
  options: DropdownOption[]
  selected: string[]
  onChange: (selected: string[]) => void
  placeholder?: string
  chipVariant?: 'default' | 'exclude'
  'data-testid'?: string
}

export function MultiSelectDropdown({
  label,
  options,
  selected,
  onChange,
  placeholder = '+ add',
  chipVariant = 'default',
  'data-testid': testId,
}: MultiSelectDropdownProps) {
  const [open, setOpen] = React.useState(false)
  const [search, setSearch] = React.useState('')

  const filteredOptions = options.filter((o) =>
    o.value.toLowerCase().includes(search.toLowerCase())
  )

  function toggle(value: string) {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value))
    } else {
      onChange([...selected, value])
    }
  }

  function removeChip(value: string, e: React.MouseEvent) {
    e.stopPropagation()
    onChange(selected.filter((v) => v !== value))
  }

  const chipClass = chipVariant === 'exclude' ? 'dash-ddchip-ex' : 'dash-ddchip'

  const labelText = selected.length > 0
    ? `${label} · ${selected.length}`
    : label

  return (
    <div className="fgroup" data-testid={testId}>
      <div
        className="flabel"
        style={{ fontSize: 15, fontWeight: 500, color: 'var(--ink-2)', marginBottom: 6, display: 'flex', justifyContent: 'space-between' }}
      >
        <span>{labelText}</span>
      </div>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={`dash-ddinput w-full text-left${open ? ' open' : ''}`}
            aria-label={`Open ${label} filter`}
            aria-haspopup="listbox"
            aria-expanded={open}
          >
            {selected.length === 0 ? (
              <span className="dash-ddplace">{placeholder}</span>
            ) : (
              selected.map((v) => (
                <span key={v} className={chipClass} data-variant={chipVariant === 'exclude' ? 'exclude' : 'include'}>
                  {v}
                  <span
                    role="button"
                    aria-label={`Remove ${v}`}
                    onClick={(e) => removeChip(v, e)}
                    style={{ cursor: 'pointer', opacity: 0.7, marginLeft: 2 }}
                  >
                    ×
                  </span>
                </span>
              ))
            )}
            <span className="dash-ddcaret">▾</span>
          </button>
        </PopoverTrigger>
        <PopoverContent
          className="brand-theme bg-[var(--surface-card)] p-0 w-[200px]"
          align="start"
          side="bottom"
          style={{ border: '1px solid var(--line-warm)', borderRadius: 0, boxShadow: '0 4px 12px rgba(0,0,0,0.06)' }}
        >
          {options.length > 6 && (
            <div style={{ borderBottom: '1px solid var(--line-warm)' }}>
              <input
                autoFocus
                placeholder={`Find ${label.toLowerCase()}…`}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{
                  width: '100%',
                  height: 28,
                  border: 'none',
                  outline: 'none',
                  padding: '0 8px',
                  fontSize: 11,
                  fontFamily: 'inherit',
                  color: 'var(--ink-2)',
                  background: 'var(--surface-card)',
                }}
              />
            </div>
          )}
          <div
            role="listbox"
            aria-multiselectable="true"
            aria-label={label}
            style={{ maxHeight: 200, overflowY: 'auto' }}
          >
            {filteredOptions.length === 0 ? (
              <div style={{ padding: '6px 8px', fontSize: 11, color: 'var(--mute)', fontStyle: 'italic' }}>
                No options
              </div>
            ) : (
              filteredOptions.map((opt) => {
                const checked = selected.includes(opt.value)
                return (
                  <div
                    key={opt.value}
                    role="option"
                    aria-selected={checked}
                    onClick={() => toggle(opt.value)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '4px 8px',
                      fontSize: 11,
                      cursor: 'pointer',
                      background: 'transparent',
                    }}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = 'var(--surface-page)' }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'transparent' }}
                  >
                    <span
                      style={{
                        width: 12,
                        height: 12,
                        border: `1px solid ${checked ? 'var(--text-primary)' : 'var(--ink-2)'}`,
                        background: checked ? 'var(--text-primary)' : 'transparent',
                        flexShrink: 0,
                        display: 'inline-block',
                      }}
                    />
                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {opt.value}
                    </span>
                    {opt.count != null && (
                      <span style={{ color: 'var(--mute)', fontSize: 10 }}>{opt.count}</span>
                    )}
                  </div>
                )
              })
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  )
}
